import {
  ExpiringDocumentsSettingPayload,
  ExpiringDocumentsSettings,
  ExpiringDocumentsFrequency,
  ExpiringDocumentType,
} from '@vyce/core/src/api/types';

import { ALL_DOCUMENT_TYPES, HOUR_ITEMS, MONTH_DAY_ITEMS, WEEKDAY_ITEMS } from './constants';

export interface OptionItem {
  name: string;
  uuid: string;
}

export interface ExpiringDocumentsFormValue {
  uuid?: string; // present once the notification has been saved on the server
  name: string; // required, 1–127 chars
  enabled: boolean;
  teamIds: string[];
  documentTypes: ExpiringDocumentType[];
  frequency: ExpiringDocumentsFrequency;
  dayOfWeek: OptionItem | null; // uuid '0'..'6', Mon=0
  dayOfMonth: OptionItem | null; // uuid '1'..'31'
  sendHour: OptionItem | null; // local hour, uuid '0'..'23'; converted to/from the API's UTC hour
  receivers: OptionItem[];
}

export interface ExpiringDocumentsFormValues {
  notifications: ExpiringDocumentsFormValue[];
}

export const EMPTY_FORM_VALUE: ExpiringDocumentsFormValue = {
  name: '',
  enabled: true,
  teamIds: [],
  documentTypes: [...ALL_DOCUMENT_TYPES],
  frequency: 'daily',
  dayOfWeek: null,
  dayOfMonth: null,
  sendHour: null,
  receivers: [],
};

/**
 * API settings -> form value.
 * Empty team_ids / document_types mean "all", so we expand them for the checkboxes.
 * The UTC `hour` is placed on a Date via setUTCHours so the hour select shows it in local time.
 */
export const settingsToFormValue = (
  settings: ExpiringDocumentsSettings,
  allTeamIds: string[],
  recipientIdToName: Record<string, string> = {}
): ExpiringDocumentsFormValue => {
  const sendTime = new Date();
  sendTime.setUTCHours(settings.hour ?? 9, 0, 0, 0);
  const sendHour = HOUR_ITEMS[sendTime.getHours()] ?? null;

  const dayOfWeek =
    settings.day_of_week !== null && settings.day_of_week !== undefined
      ? WEEKDAY_ITEMS[settings.day_of_week] ?? null
      : null;

  const dayOfMonth =
    settings.day_of_month !== null && settings.day_of_month !== undefined
      ? MONTH_DAY_ITEMS[settings.day_of_month - 1] ?? null
      : null;

  return {
    uuid: settings.uuid,
    name: settings.name ?? '',
    enabled: settings.enabled ?? false,
    teamIds: settings.team_ids?.length ? settings.team_ids : allTeamIds,
    documentTypes: settings.document_types?.length ? settings.document_types : [...ALL_DOCUMENT_TYPES],
    frequency: settings.frequency ?? 'daily',
    dayOfWeek,
    dayOfMonth,
    sendHour,
    receivers: (settings.recipient_user_ids ?? []).map(id => ({
      uuid: id,
      name: recipientIdToName[id] ?? id,
    })),
  };
};

/**
 * Form value -> API payload (the full object; PUT replaces everything, so we never send a partial).
 * All teams/document types selected collapse back to [] ("all"). We always send the day field that
 * matches the frequency (and null the other) to avoid the backend 422. `uuid` is never sent — the
 * server reads it from the URL.
 */
export const formValueToPayload = (
  form: ExpiringDocumentsFormValue,
  allTeamsCount: number
): ExpiringDocumentsSettingPayload => {
  let hour = 9;
  if (form.sendHour) {
    const sendTime = new Date();
    sendTime.setHours(Number(form.sendHour.uuid), 0, 0, 0);
    hour = sendTime.getUTCHours();
  }
  const allTeamsSelected = allTeamsCount > 0 && form.teamIds.length === allTeamsCount;
  const allDocsSelected = form.documentTypes.length === ALL_DOCUMENT_TYPES.length;

  return {
    name: form.name.trim(),
    enabled: form.enabled,
    frequency: form.frequency,
    day_of_week: form.frequency === 'weekly' && form.dayOfWeek ? Number(form.dayOfWeek.uuid) : null,
    day_of_month:
      form.frequency === 'specific_day' && form.dayOfMonth ? Number(form.dayOfMonth.uuid) : null,
    hour,
    recipient_user_ids: form.receivers.map(r => r.uuid),
    team_ids: allTeamsSelected ? [] : form.teamIds,
    document_types: allDocsSelected ? [] : form.documentTypes,
  };
};
